import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, TextareaAutosize } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FunctionComponent } from "react"
import { useCallback, useState } from "react"
import { useMutation } from "urql"

import { Button } from "@/components/common/Button"
import type { CommentMutation, CommentMutationCreateArgs, CommentParentTableType } from "@/graphql/codegen/graphql"
import { CreateCommentMutationDocument } from "@/graphql/codegen/graphql"
import type { DialogCloseAction } from "@/types/common"

interface AddContactCommentProps {
  referenceId: string
  dialogTitle?: string
  type: CommentParentTableType
  onClose: (action: DialogCloseAction) => void
  onSubmit: () => void
}

const AddCommentDialog: FunctionComponent<AddContactCommentProps> = ({
  referenceId,
  type,
  dialogTitle = "Add Comment",
  onClose,
  onSubmit,
}) => {
  const [commentValue, setCommentValue] = useState<string>("")
  const [{ fetching }, createComment] = useMutation<CommentMutation, CommentMutationCreateArgs>(
    CreateCommentMutationDocument
  )

  const handleSubmit = useCallback(async () => {
    await createComment({
      input: {
        referenceId: referenceId,
        type,
        value: commentValue ?? "",
        isActive: true,
      },
    })
    onSubmit()
    onClose("success")
  }, [createComment, referenceId, type, commentValue, onSubmit, onClose])

  const handleResetClick = useCallback(() => {
    onClose("cancel")
  }, [onClose])

  return (
    <Dialog
      open
      onClose={(_, reason) => {
        onClose(reason)
      }}
      fullWidth
    >
      <DialogTitle className='h-[60px] pb-3 pl-10 font-normal text-primary'>{dialogTitle}</DialogTitle>
      <IconButton
        aria-label='close'
        onClick={() => {
          onClose("cancel")
        }}
        className='absolute right-4 top-4 text-primary'
      >
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>
        <TextareaAutosize
          minRows={3.3}
          maxRows={3.3}
          className='w-full overflow-y-auto rounded-md border border-solid border-gray-400 p-4 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#BFDBFE]'
          value={commentValue}
          onChange={(e) => setCommentValue(e.target.value)}
          placeholder='Add a comment...'
        />
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={handleResetClick}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!commentValue || fetching}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddCommentDialog
